import { Breakpoints, BreakpointWrapper, Container, ContainerPadding, CTA, Layout, LayoutRowGap, Tabs, Text, TextAlignment, TokenTextAppearance, TokenTextColor } from '@volkswagen-onehub/components-core';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getHorariosInstallation } from 'src/bff/api/get-installations';
import { Loading, MultipleCTASelection } from 'src/components';
import { AllDaysWrapper, CalendarHandlerWrapper, CalendarioContainer, Capitalize, DayPositionWrapper, DayWrapper, FranjaContainer, GreyText, HorasContainer } from 'src/forms/Cem/CEMSegundaEntrega/CitaCEMSegundaEntregaStyles';
import { ChevronLeft, ChevronRight, Info } from 'src/icons-core-imports';
import { Day, EntregaIdealHorarioResponse, OneFormState } from 'src/types';

interface CitaCEMSegundaEntregaProps {
	fechaName: string;
	horaName: string;
}

export const CitaCEMSegundaEntrega = (props: CitaCEMSegundaEntregaProps) => {
	const { fechaName, horaName } = props;

	const dispatch = useDispatch();
	const tabsRef = useRef<HTMLDivElement>(null);

	const { formData, dealersInfo } = useSelector((state: OneFormState) => state);
	const { fields } = formData;
	const { selectedInstallation } = dealersInfo;

	const weekDays = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
	const [monthCalendarDays, setMonthCalendarDays] = useState<Day[]>(null);
	const [previousMonthBtn, setPreviousMonthBtn] = useState<boolean>(false);
	const [nextMonthBtn, setNextMonthBtn] = useState<boolean>(true);

	const today = dayjs(new Date());
	const [startDay, setStartDay] = useState<Dayjs>(today);
	const [endDay, setEndDay] = useState<Dayjs>(today);
	const [isPreselected, setIsPreselected] = useState<boolean>(false);
	const [selectedDay, setSelectedDay] = useState<Dayjs>(fields[fechaName] ? dayjs(fields[fechaName], 'YYYY-MM-DD') : null);
	const [availableDays, setAvailableDays] = useState<EntregaIdealHorarioResponse[]>([]);
	const [showMonth, setShowMonth] = useState<Dayjs>(selectedDay);

	const [franjasManana, setFranjasManana] = useState([]);
	const [franjasTarde, setFranjasTarde] = useState([]);
	const [selectedFranja, setSelectedFranja] = useState(null);
	const [infoFranjasManana, setInfoFranjasManana] = useState<string>('');
	const [infoFranjasTarde, setInfoFranjasTarde] = useState<string>('');
	const [showFranja, setShowFranja] = useState<number>(0);
	const [resetFranjas, setResetFranjas] = useState<boolean>(false);

	useEffect(() => {
		getHorarios(today);
	}, []);

	// CALENDARIO

	useEffect(() => {
		if (startDay && endDay && selectedDay && showMonth) {
			handleBuildCalendar();
		}
	}, [startDay, endDay, selectedDay, showMonth]);

	const getHorarios = async (dayJs: Dayjs) => {
		if (!selectedInstallation?.VGED_DealerCode__c || availableDays.length > 0) {
			return;
		}
		const thisMonth = await getHorariosInstallation(selectedInstallation.VGED_DealerCode__c, dayJs.format('YYYY'), dayJs.format('MM'));
		const nextMonth = await getHorariosInstallation(selectedInstallation.VGED_DealerCode__c, dayJs.add(1, 'month').format('YYYY'), dayJs.add(1, 'month').format('MM'));

		let day = null;
		let days = [];
		if (thisMonth && thisMonth.length > 0) {
			day = createDay(thisMonth[0].date);
			days = thisMonth.concat(nextMonth);
		} else if (nextMonth && nextMonth.length > 0) {
			day = createDay(nextMonth[0].date);
			const anotherMonth = await getHorariosInstallation(selectedInstallation.VGED_DealerCode__c, dayJs.add(2, 'month').format('YYYY'), dayJs.add(2, 'month').format('MM'));
			days = nextMonth.concat(anotherMonth);
		}

		setAvailableDays(days);
		setStartDay(day);
		setEndDay(createDay(days[days.length - 1].date));
		setSelectedDay(fields[fechaName] ? createDay(fields[fechaName]) : day);
		setShowMonth(fields[fechaName] ? createDay(fields[fechaName]) : day);
	};

	const handleBuildCalendar = () => {
		const year = Number(showMonth.format('YYYY'));
		const month = Number(showMonth.format('M')) - 1;

		const firstMonthDay = dayjs().year(year).month(month).date(1);
		const firstWeekDay = firstMonthDay.format('d'); // The day of the week, with Sunday as 0
		const daysInMonth = dayjs().year(year).month(month).daysInMonth();

		let calendarDays: Day[] = [];

		// Se genera una array con espacios vacios para rellenar la primera fila del calendario y sincronizarla con la columna del dia de la semana
		if (firstWeekDay === '0') {
			calendarDays = new Array(6).fill('');
		} else {
			calendarDays = new Array(parseInt(firstWeekDay) - 1).fill('');
		}

		let dayJs = firstMonthDay;

		for (let i = 1; i <= daysInMonth; i++) {
			const isActive = selectedDay ? selectedDay.isSame(dayJs, 'day') : false;
			calendarDays.push(createCalendarDay(dayJs, isActive));
			dayJs = dayJs.add(1, 'day'); // Increment 1 day
		}

		setMonthCalendarDays(calendarDays);

		const findFirstAvailableDay = calendarDays.find((d) => d && typeof d === 'object' && d.disabled == false);
		if (!isPreselected && findFirstAvailableDay && !fields[fechaName]) {
			const availableDay = createDay(findFirstAvailableDay.date);

			dispatch({ type: 'UPDATE_FIELDS', payload: { [fechaName]: availableDay?.format('YYYY-MM-DD') } });
			handleBuildFranjas(availableDay);
			setSelectedDay(availableDay);
			setIsPreselected(true);
		} else if (fields[fechaName]) {
			handleBuildFranjas(createDay(fields[fechaName]));
		}
	};

	const createCalendarDay = (dayJs: Dayjs, active = false) => {
		const objDay: Day = {
			dayNumber: dayJs.format('D'), // 1 - 31
			dayOfTheWeek: dayJs.format('d'), // 0 - 6
			date: dayJs.format('YYYY-MM-DD'),
			disabled: isDayDisabled(dayJs),
			active: active,
		};
		return objDay;
	};

	const createDay = (str: string) => {
		return dayjs(str, 'YYYY-MM-DD');
	};

	const isDayDisabled = (dayJs: Dayjs) => {
		if (endDay && dayJs.isAfter(endDay, 'day')) {
			return true;
		} else if (dayJs.isBefore(startDay, 'day')) {
			return true;
		}
		return !availableDays.some((item) => item.date === dayJs.format('YYYY-MM-DD'));
	};

	const getOffsetMonth = (n: number) => {
		if (!showMonth) {
			return today;
		} else if (n > 0) {
			return showMonth.add(n, 'month');
		} else if (n < 0) {
			return showMonth.subtract(n * -1, 'month');
		} else {
			return showMonth;
		}
	};

	const handleNextMonth = (e: any) => {
		// Carga del siguiente mes
		e.preventDefault();
		if (nextMonthBtn) {
			setIsPreselected(false);
			dispatch({ type: 'UPDATE_FIELDS', payload: { [fechaName]: null } }); // Reset fecha seleccionada
			setShowMonth(getOffsetMonth(1));
		}
	};

	const handlePreviousMonth = (e: any) => {
		// Carga del anterior mes
		e.preventDefault();
		if (previousMonthBtn) {
			setIsPreselected(false);
			dispatch({ type: 'UPDATE_FIELDS', payload: { [fechaName]: null } }); // Reset fecha seleccionada
			setShowMonth(getOffsetMonth(-1));
		}
	};

	const handleSelectDay = (day: Day) => {
		const dayJs = createDay(day.date);
		dispatch({ type: 'UPDATE_FIELDS', payload: { [fechaName]: dayJs.format('YYYY-MM-DD') } });
		setSelectedDay(dayJs); // Seleccion del dia
		handleBuildFranjas(dayJs);
		setShowFranja(0); // Mostramos "mañana" para reiniciar franjas, dentro de handleBuildFranjas() no funciona
	};

	useEffect(() => {
		if (showMonth && startDay && endDay) {
			setPreviousMonthBtn(startDay.isBefore(showMonth, 'month'));
			setNextMonthBtn(endDay.isAfter(showMonth, 'month'));
		}
	}, [showMonth, startDay, endDay]);

	// FRANJAS HORARIAS

	const handleBuildFranjas = (dayJs?: Dayjs, clickOnFranja?: string) => {
		handleResetFranjas();

		const day = dayJs || selectedDay;
		const turnos = availableDays.find((item) => item.date === day.format('YYYY-MM-DD'))?.turnos;
		if (!turnos) return;

		const manana = [];
		const tarde = [];

		turnos.forEach((turno) => {
			const slots = generateTimeSlots(turno.from, turno.to);
			slots.forEach((slot) => {
				const time = dayjs(`1970-01-01T${slot.label}`);
				if (time.isBefore(dayjs('1970-01-01T15:00'))) {
					manana.push(slot);
				} else {
					tarde.push(slot);
				}
			});
		});
		setFranjasManana(manana);
		setFranjasTarde(tarde);

		let franja = null;
		// Primero seleccionamos la primera franja disponible de mañana o de tarde
		if (manana.length > 0) {
			franja = manana[0].value;
		} else if (tarde.length > 0) {
			franja = tarde[0].value;
		}

		// Si ya tenemos la hora seleccionada (navegacion steps?) seleccionamos la franja
		if (fields[horaName] && !selectedFranja) {
			franja = fields[horaName];
			if (dayjs(fields[horaName], 'HH').isAfter(dayjs('15', 'HH'))) {
				setShowFranja(1);
			}
		} else if (clickOnFranja) {
			franja = fields[horaName]; // Necesario para no perder la hora si navegamos entre franjas
		}
		setSelectedFranja(franja);
		setTimeout(() => {
			dispatch({ type: 'UPDATE_FIELDS', payload: { [horaName]: franja } });
		}, 20);

		if (manana.length > 0) {
			setInfoFranjasManana('La duración de la cita será de una hora aproximadamente.');
		} else {
			setInfoFranjasManana('No hay horas disponibles.');
		}

		if (tarde.length > 0) {
			setInfoFranjasTarde('La duración de la cita será de una hora aproximadamente.');
		} else {
			setInfoFranjasTarde('No hay horas disponibles.');
		}
	};

	const generateTimeSlots = (from, to) => {
		const slots = [];
		let current = dayjs(`1970-01-01T${from}`);
		const end = dayjs(`1970-01-01T${to}`);

		while (current.isBefore(end)) {
			const label = current.format('HH:mm');
			slots.push({ label, value: label });
			current = current.add(30, 'minute');
		}

		return slots;
	};

	const handleResetFranjas = () => {
		setResetFranjas(true);
		setTimeout(() => {
			setResetFranjas(false);
		}, 10);
	};

	const handleChangeFranja = (index: number) => {
		setShowFranja(index);
		handleBuildFranjas(null, index == 1 ? 'tarde' : 'manana');
	};

	return availableDays.length == 0 ? (
		<Loading />
	) : (
		<Layout
			appearance={{
				[Breakpoints.default]: [
					{ name: '.', columns: 2 },
					{ name: 'a', columns: 20 },
					{ name: '.', columns: 2 },
					{ name: '.', columns: 4 },
					{ name: 'b', columns: 16 },
					{ name: '.', columns: 4 },
				],
				[Breakpoints.b960]: [
					{ name: '.', columns: 2 },
					{ name: 'a', columns: 12 },
					{ name: '.', columns: 2 },
					{ name: 'b', columns: 6 },
					{ name: '.', columns: 2 },
				],
			}}
		>
			<CalendarioContainer>
				<CalendarHandlerWrapper className="calendar-handler-wrapper">
					<CTA tag="button" emphasis="tertiary" onClick={handlePreviousMonth} disabled={!previousMonthBtn} ariaLabel="aria label" icon={<ChevronLeft />} />
					<BreakpointWrapper min={Breakpoints.b960}>
						<Capitalize onClick={handlePreviousMonth} isDisabled={!previousMonthBtn}>
							{getOffsetMonth(-1).locale('es').format('MMMM')}
						</Capitalize>
					</BreakpointWrapper>
					<Text bold appearance={TokenTextAppearance.copy200}>
						<Capitalize isDisabled={false} hasTitle>
							{showMonth ? showMonth.locale('es').format('MMMM') : null} {showMonth ? showMonth.format('YYYY') : null}
						</Capitalize>
					</Text>
					<BreakpointWrapper min={Breakpoints.b960}>
						<Capitalize onClick={handleNextMonth} isDisabled={!nextMonthBtn}>
							{getOffsetMonth(1).locale('es').format('MMMM')}
						</Capitalize>
					</BreakpointWrapper>
					<CTA tag="button" emphasis="tertiary" onClick={handleNextMonth} disabled={!nextMonthBtn} ariaLabel="aria label" icon={<ChevronRight />} />
				</CalendarHandlerWrapper>
				<AllDaysWrapper className="all-days-wrapper">
					<Layout
						appearance={{
							[Breakpoints.default]: [
								{ name: '.', columns: 1 },
								{ name: 'a', columns: 3 },
								{ name: 'b', columns: 3 },
								{ name: 'c', columns: 3 },
								{ name: 'd', columns: 3 },
								{ name: 'e', columns: 3 },
								{ name: 'f', columns: 3 },
								{ name: 'g', columns: 3 },
								{ name: '.', columns: 2 },
							],
							[Breakpoints.b560]: [
								{ name: '.', columns: 2 },
								{ name: 'a', columns: 2 },
								{ name: '.', columns: 1 },
								{ name: 'b', columns: 2 },
								{ name: '.', columns: 1 },
								{ name: 'c', columns: 2 },
								{ name: '.', columns: 1 },
								{ name: 'd', columns: 2 },
								{ name: '.', columns: 1 },
								{ name: 'e', columns: 2 },
								{ name: '.', columns: 1 },
								{ name: 'f', columns: 2 },
								{ name: '.', columns: 1 },
								{ name: 'g', columns: 2 },
								{ name: '.', columns: 1 },
							],
							[Breakpoints.b960]: [
								{ name: '.', columns: 1 },
								{ name: 'a', columns: 3 },
								{ name: 'b', columns: 3 },
								{ name: 'c', columns: 3 },
								{ name: 'd', columns: 3 },
								{ name: 'e', columns: 3 },
								{ name: 'f', columns: 3 },
								{ name: 'g', columns: 3 },
								{ name: '.', columns: 2 },
							],
						}}
						rowGap={LayoutRowGap.static250}
					>
						{weekDays.map((day, index) => {
							if (index > 4) {
								return (
									<DayPositionWrapper key={`weekday-${index}`}>
										<DayWrapper className="day-wrapper">
											<Text bold appearance={TokenTextAppearance.copy200}>
												{day}
											</Text>
										</DayWrapper>
									</DayPositionWrapper>
								);
							} else {
								return (
									<DayPositionWrapper key={`weekday-${index}`}>
										<DayWrapper className="day-wrapper">
											<Text appearance={TokenTextAppearance.copy200}>
												<GreyText>{day}</GreyText>
											</Text>
										</DayWrapper>
									</DayPositionWrapper>
								);
							}
						})}
						{monthCalendarDays
							? monthCalendarDays.map((day, index) => {
									return (
										<DayPositionWrapper key={`monthday-${index}`}>
											<DayWrapper
												onClick={(e) => {
													e.preventDefault();
													if (!day.disabled) {
														if (tabsRef && tabsRef.current) {
															tabsRef.current.scrollIntoView({ behavior: 'smooth' });
														}
														handleSelectDay(day);
													}
												}}
												active={day.active}
												disabled={day.disabled}
												className="day-wrapper"
											>
												<Text appearance={TokenTextAppearance.copy200} color={TokenTextColor.inherit}>
													{day.dayNumber}
												</Text>
											</DayWrapper>
										</DayPositionWrapper>
									);
							  })
							: null}
					</Layout>
				</AllDaysWrapper>
			</CalendarioContainer>

			<FranjaContainer>
				<Text appearance={TokenTextAppearance.copy200} textAlign={TextAlignment.center}>
					Selecciona la franja horaria
				</Text>
				<HorasContainer>
					<div
						onClick={(e) => {
							e.preventDefault();
						}}
					>
						<Tabs
							idPrefix="franjas"
							defaultIndex={showFranja}
							variant="underlined"
							tabBarVerticalPosition="bottom"
							onChange={(index) => {
								handleChangeFranja(index);
							}}
						>
							{{
								title: <Text>Mañana</Text>,
								content: (
									<Layout allowOverflowingContent appearance={{ [Breakpoints.default]: [{ name: 'a', columns: 24 }] }}>
										<>
											<MultipleCTASelection reset={resetFranjas} ctaValues={franjasManana} name={horaName} />
											<Container padding={{ top: ContainerPadding.static300 }}>
												<Text appearance={TokenTextAppearance.copy200}>
													<Info variant="small" /> {infoFranjasManana}
												</Text>
											</Container>
										</>
									</Layout>
								),
								key: 'MANANA',
							}}
							{{
								title: <Text>Tarde</Text>,
								content: (
									<Layout allowOverflowingContent appearance={{ [Breakpoints.default]: [{ name: 'a', columns: 24 }] }}>
										<>
											<MultipleCTASelection reset={resetFranjas} ctaValues={franjasTarde} name={horaName} />
											<Container padding={{ top: ContainerPadding.static300 }}>
												<Text appearance={TokenTextAppearance.copy200}>
													<Info variant="small" /> {infoFranjasTarde}
												</Text>
											</Container>
										</>
									</Layout>
								),
								key: 'TARDE',
							}}
						</Tabs>
					</div>
				</HorasContainer>
			</FranjaContainer>
		</Layout>
	);
};
